import api from "./api";

// Système de cache pour optimiser les performances
const cache = {
  contracts: new Map(),
  filterOptions: null,
  contractById: new Map(),
  contractChanges: new Map(),
  cacheTime: 60000, // 1 minute de validité pour le cache
  maxSize: 30, // Nombre maximum d'entrées dans le cache

  // AJOUT : Cache spécialisé pour les filtres (plus long car données stables)
  filterCache: {
    baseOptions: null, // Cache pour agences + années (ne change que rarement)
    monthsCache: new Map(), // Cache pour mois par année
    daysCache: new Map(), // Cache pour jours par année/mois
    cacheTime: 300000, // 5 minutes pour les filtres (plus long)
    timestamp: 0,
  },
};

// Fonction utilitaire pour nettoyer les paramètres
const cleanParams = (params) => {
  const cleaned = {};
  for (const key in params) {
    if (
      params[key] !== "" &&
      params[key] !== undefined &&
      params[key] !== null
    ) {
      cleaned[key] = params[key];
    }
  }
  return cleaned;
};

// Fonction utilitaire pour limiter la taille du cache
const limitCacheSize = (cacheMap, maxSize) => {
  if (cacheMap.size > maxSize) {
    // Suppression des entrées les plus anciennes (FIFO)
    const keysToDelete = Array.from(cacheMap.keys()).slice(
      0,
      cacheMap.size - maxSize
    );
    keysToDelete.forEach((key) => cacheMap.delete(key));
  }
};

// Service pour les contrats avec optimisations
const contractService = {
  // Obtenir la liste des contrats avec filtres
// Obtenir la liste des contrats avec filtres
getContracts: async (params) => {
  try {
    // Nettoyer les paramètres vides
    const cleanedParams = cleanParams(params);

    // Générer une clé de cache basée sur les paramètres
    const cacheKey = JSON.stringify(cleanedParams);

    // Vérifier si la réponse est en cache et si elle est encore valide
    const cachedData = cache.contracts.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < cache.cacheTime) {
      console.log("Utilisation du cache pour les contrats:", cleanedParams);
      return cachedData.data;
    }

    console.log("Récupération des contrats depuis l'API:", cleanedParams);
    const response = await api.get("/contracts", { params: cleanedParams });

    // Stocker la réponse dans le cache
    cache.contracts.set(cacheKey, {
      data: response.data,
      timestamp: Date.now(),
    });

    // Limiter la taille du cache
    limitCacheSize(cache.contracts, cache.maxSize);

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la récupération des contrats:", error);
    return { success: false, message: error.message };
  }
},

  // Obtenir un contrat spécifique
  getContract: async (id) => {
    try {
      // Vérifier si le contrat est en cache et si il est encore valide
      const cachedContract = cache.contractById.get(id);
      if (
        cachedContract &&
        Date.now() - cachedContract.timestamp < cache.cacheTime
      ) {
        console.log("Utilisation du cache pour le contrat:", id);
        return cachedContract.data;
      }

      console.log("Récupération du contrat depuis l'API:", id);
      const response = await api.get(`/contracts/${id}`);

      // Stocker la réponse dans le cache
      cache.contractById.set(id, {
        data: response.data,
        timestamp: Date.now(),
      });

      // Limiter la taille du cache
      limitCacheSize(cache.contractById, cache.maxSize);

      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération du contrat:", error);
      return { success: false, message: error.message };
    }
  },

  // Obtenir les totaux pour les contrats filtrés
// Obtenir les totaux pour les contrats filtrés
getTotals: async (params) => {
  try {
    // Nettoyer les paramètres vides
    const cleanedParams = cleanParams(params);

    // Générer une clé de cache basée sur les paramètres
    const cacheKey = "totals-" + JSON.stringify(cleanedParams);

    // Vérifier si les totaux sont en cache et s'ils sont encore valides
    const cachedData = cache.contracts.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < cache.cacheTime) {
      console.log("Utilisation du cache pour les totaux");
      return cachedData.data;
    }

    console.log("Récupération des totaux depuis l'API");
    const response = await api.get("/contracts/totals", {
      params: cleanedParams,
    });

    console.log("Réponse des totaux:", response.data);

    // Stocker la réponse dans le cache
    cache.contracts.set(cacheKey, {
      data: response.data,
      timestamp: Date.now(),
    });

    // Limiter la taille du cache
    limitCacheSize(cache.contracts, cache.maxSize);

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la récupération des totaux:", error);
    // En cas d'erreur, renvoyer des totaux vides
    return {
      success: false,
      message: error.message,
      data: {
        contractsCreated: [],
        paymentStates: [],
        paymentStatesTotals: {
          totalReceived: 0,
          dailyReceived: 0,
          totalDiscount: 0,
          count: 0,
        },
        statistics: {
          totalContracts: 0,
          totalWeights: { jewelWeight: 0, cdvaWeight: 0, cddWeight: 0 },
          agenciesCount: 0,
          uniqueAgencies: [],
          dateRange: { min: null, max: null },
          financialTotals: {
            disbursementAmount: 0,
            expectedAmount: 0,
            receivedAmount: 0,
            remainingAmount: 0,
            discount: 0,
          },
          renewalStats: {
            renewed: 0,
            notRenewed: 0,
            renewalPercentage: 0,
          },
          paymentStats: {
            contractsWithPayment: 0,
            contractsWithDiscount: 0,
            averageExpectedAmount: 0,
          },
        },
      },
    };
  }
},

  // Obtenir les statistiques détaillées des contrats
  getStatistics: async (params) => {
    try {
      // Nettoyer les paramètres vides
      const cleanedParams = cleanParams(params);

      // Générer une clé de cache basée sur les paramètres avec préfixe 'stats'
      const cacheKey = "statistics-" + JSON.stringify(cleanedParams);

      // Vérifier si les statistiques sont en cache et si elles sont encore valides
      const cachedData = cache.contracts.get(cacheKey);
      if (cachedData && Date.now() - cachedData.timestamp < cache.cacheTime) {
        console.log(
          "Utilisation du cache pour les statistiques:",
          cleanedParams
        );
        return cachedData.data;
      }

      console.log("Récupération des statistiques depuis l'API:", cleanedParams);
      const response = await api.get("/contracts/statistics", {
        params: cleanedParams,
      });

      console.log("Réponse des statistiques:", response.data);

      // Valider la structure de la réponse
      if (!response.data.success || !response.data.data) {
        throw new Error("Structure de réponse invalide pour les statistiques");
      }

      // Stocker la réponse dans le cache
      cache.contracts.set(cacheKey, {
        data: response.data,
        timestamp: Date.now(),
      });

      // Limiter la taille du cache
      limitCacheSize(cache.contracts, cache.maxSize);

      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error);

      // En cas d'erreur, renvoyer une structure de données par défaut
      // pour éviter que l'interface se casse
      return {
        success: false,
        message: error.message,
        data: {
          basicMetrics: {
            totalContracts: 0,
            dateRange: { min: null, max: null },
            agenciesCount: 0,
            uniqueAgencies: [],
          },
          financialStats: {
            totals: {
              disbursementAmount: 0,
              expectedAmount: 0,
              receivedAmount: 0,
              remainingAmount: 0,
              discount: 0,
            },
            averages: {
              averagePerContract: 0,
            },
            ratios: {
              recoveryRate: 0,
              paidContractsPercentage: 0,
              overdueContractsPercentage: 0,
            },
          },
          temporalStats: {
            monthlyDistribution: [],
            averageRepaymentDelay: 0,
          },
          agencyStats: {
            distributionByAgency: [],
            dayAgencyHeatmap: [],
            weightsByAgency: [],
            renewalByAgency: [],
          },
          weightStats: {
            averageJewelWeight: 0,
            totalWeights: {
              jewelWeight: 0,
              cdvaWeight: 0,
              cddWeight: 0,
            },
            cdvaVsCddRatio: {
              cdvaPercentage: 0,
              cddPercentage: 0,
            },
            weightsByAgency: [],
          },
          performanceStats: {
            renewalRate: {
              renewed: 0,
              notRenewed: 0,
              total: 0,
              renewalPercentage: 0,
            },
            renewalByAgency: [],
            discountStats: {
              contractsWithDiscount: 0,
              averageDiscount: 0,
              totalDiscount: 0,
              discountPercentage: 0,
            },
            commentsStats: {
              contractsWithComments: 0,
              commentsPercentage: 0,
            },
          },
          alerts: {
            overdueContracts: 0,
            contractsToWatch: 0,
            negativeRemainingCount: 0,
            negativeRemainingAmount: 0,
            overdueDetails: [],
            watchDetails: [],
          },
        },
      };
    }
  },

  // Obtenir les états journaliers des contrats
  getDailyStates: async (params) => {
    try {
      // Nettoyer les paramètres vides
      const cleanedParams = cleanParams(params);

      // Générer une clé de cache basée sur les paramètres avec préfixe 'daily-states'
      const cacheKey = "daily-states-" + JSON.stringify(cleanedParams);

      // Vérifier si les états journaliers sont en cache et s'ils sont encore valides
      const cachedData = cache.contracts.get(cacheKey);
      if (cachedData && Date.now() - cachedData.timestamp < cache.cacheTime) {
        console.log(
          "Utilisation du cache pour les états journaliers:",
          cleanedParams
        );
        return cachedData.data;
      }

      console.log(
        "Récupération des états journaliers depuis l'API:",
        cleanedParams
      );
      const response = await api.get("/contracts/daily-states", {
        params: cleanedParams,
      });

      console.log("Réponse des états journaliers:", response.data);

      // Valider la structure de la réponse
      if (!response.data.success || !response.data.data) {
        throw new Error(
          "Structure de réponse invalide pour les états journaliers"
        );
      }

      // Stocker la réponse dans le cache
      cache.contracts.set(cacheKey, {
        data: response.data,
        timestamp: Date.now(),
      });

      // Limiter la taille du cache
      limitCacheSize(cache.contracts, cache.maxSize);

      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des états journaliers:",
        error
      );

      // En cas d'erreur, renvoyer une structure de données par défaut
      return {
        success: false,
        message: error.message,
        data: {
          contractsCreated: [],
          paymentStates: [],
          statistics: {
            totalContracts: 0,
            totalWeights: { jewelWeight: 0, cdvaWeight: 0, cddWeight: 0 },
            agenciesCount: 0,
            uniqueAgencies: [],
            dateRange: { min: null, max: null },
            financialTotals: {
              expectedAmount: 0,
              receivedAmount: 0,
              remainingAmount: 0,
              discount: 0,
            },
            renewalStats: {
              renewed: 0,
              notRenewed: 0,
              renewalPercentage: 0,
            },
            paymentStats: {
              contractsWithPayment: 0,
              contractsWithDiscount: 0,
              averageExpectedAmount: 0,
            },
          },
        },
      };
    }
  },

  // Mettre à jour un contrat
  updateContract: async (id, data) => {
    try {
      const response = await api.put(`/contracts/${id}`, data);

      // Invalider les entrées de cache concernées
      cache.contractById.delete(id);

      // Invalidation du cache des listes de contrats
      for (const [key, cacheEntry] of cache.contracts.entries()) {
        if (
          cacheEntry.data &&
          cacheEntry.data.data &&
          Array.isArray(cacheEntry.data.data)
        ) {
          const contractExists = cacheEntry.data.data.some((c) => c._id === id);
          if (contractExists) {
            cache.contracts.delete(key);
          }
        }
      }

      // Invalider le cache des statistiques car les données ont changé
      contractService.invalidateStatisticsCache();

      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du contrat:", error);
      return { success: false, message: error.message };
    }
  },

  // Ajouter un commentaire
  addComment: async (contractId, text) => {
    try {
      const response = await api.post(`/contracts/${contractId}/comments`, {
        text,
      });

      // Invalider le cache du contrat commenté
      cache.contractById.delete(contractId);

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          "Erreur lors de l'ajout du commentaire",
      };
    }
  },

  // Récupération des commentaires d'un contrat
  getComments: async (contractId) => {
    try {
      const response = await api.get(`/contracts/${contractId}/comments`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          "Erreur lors de la récupération des commentaires",
      };
    }
  },

  // Suppression d'un commentaire
  deleteComment: async (contractId, commentId) => {
    try {
      console.log(
        `Deleting comment: contractId=${contractId}, commentId=${commentId}`
      );
      const response = await api.delete(
        `/contracts/${contractId}/comments/${commentId}`
      );

      // Invalider le cache du contrat modifié
      cache.contractById.delete(contractId);

      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error("Erreur de suppression commentaire:", error);
      return {
        success: false,
        error:
          error.response?.data?.error ||
          "Erreur lors de la suppression du commentaire",
      };
    }
  },

  // Récupération des utilisateurs pour les mentions
  // 🔧 CORRECTION: Supprimez les lignes 420-448 et remplacez toute la section getUsersForMentions par ceci :

  // Récupération des utilisateurs pour les mentions
  getUsersForMentions: async () => {
    try {
      const response = await api.get("/users/mentions");
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.error ||
          "Erreur lors de la récupération des utilisateurs",
      };
    }
  },

  // 🔧 AJOUT: Forcer le rafraîchissement d'un contrat depuis l'API (sans cache)
  forceRefreshContract: async (id) => {
    try {
      console.log("🔄 Rafraîchissement forcé du contrat:", id);

      // Invalider d'abord le cache
      cache.contractById.delete(id);

      // Invalider aussi les listes qui contiennent ce contrat
      for (const [key, cacheEntry] of cache.contracts.entries()) {
        if (
          cacheEntry.data &&
          cacheEntry.data.data &&
          Array.isArray(cacheEntry.data.data)
        ) {
          const contractExists = cacheEntry.data.data.some((c) => c._id === id);
          if (contractExists) {
            cache.contracts.delete(key);
          }
        }
      }

      // Récupérer depuis l'API avec timestamp pour éviter le cache navigateur
      const response = await api.get(`/contracts/${id}?_t=${Date.now()}`);

      // Stocker immédiatement dans le cache
      cache.contractById.set(id, {
        data: response.data,
        timestamp: Date.now(),
      });

      console.log("✅ Contrat rafraîchi avec succès:", response.data);
      return response.data;
    } catch (error) {
      console.error("❌ Erreur lors du rafraîchissement du contrat:", error);
      return { success: false, message: error.message };
    }
  },

  // Obtenir l'historique des modifications
  getContractChanges: async (id) => {
    try {
      // Vérifier si l'historique est en cache et s'il est encore valide
      const cachedChanges = cache.contractChanges.get(id);
      if (
        cachedChanges &&
        Date.now() - cachedChanges.timestamp < cache.cacheTime
      ) {
        return cachedChanges.data;
      }

      const response = await api.get(`/contracts/${id}/changes`);

      // Stocker la réponse dans le cache
      cache.contractChanges.set(id, {
        data: response.data,
        timestamp: Date.now(),
      });

      // Limiter la taille du cache
      limitCacheSize(cache.contractChanges, cache.maxSize);

      return response.data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération de l'historique des modifications:",
        error
      );
      return { success: false, message: error.message };
    }
  },

  // Obtenir les options de filtres
  getFilterOptions: async () => {
    try {
      // Vérifier si les options sont en cache et si elles sont encore valides
      if (
        cache.filterOptions &&
        Date.now() - cache.filterOptions.timestamp < cache.cacheTime
      ) {
        console.log("Utilisation du cache pour les options de filtres");
        return cache.filterOptions.data;
      }

      console.log("Récupération des options de filtres depuis l'API");

      // Récupérer les agences depuis l'endpoint existant
      const contractsResponse = await api.get("/contracts/filters");

      // Essayer de récupérer les années et mois depuis l'endpoint spécifique
      let yearsAndMonths = { years: [], months: [] };
      try {
        const dashboardResponse = await api.get("/dashboard/filter-options");
        if (dashboardResponse.data.success) {
          yearsAndMonths = dashboardResponse.data.data;
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des années et mois:",
          error
        );
      }

      // Si nous n'avons pas réussi à obtenir les années, créer une liste par défaut
      if (!yearsAndMonths.years || yearsAndMonths.years.length === 0) {
        const currentYear = new Date().getFullYear();
        yearsAndMonths.years = [];
        for (let i = 0; i < 5; i++) {
          yearsAndMonths.years.push((currentYear - i).toString());
        }
      }

      // Si nous n'avons pas réussi à obtenir les mois, créer une liste par défaut
      if (!yearsAndMonths.months || yearsAndMonths.months.length === 0) {
        yearsAndMonths.months = [
          { value: "1", label: "Janvier" },
          { value: "2", label: "Février" },
          { value: "3", label: "Mars" },
          { value: "4", label: "Avril" },
          { value: "5", label: "Mai" },
          { value: "6", label: "Juin" },
          { value: "7", label: "Juillet" },
          { value: "8", label: "Août" },
          { value: "9", label: "Septembre" },
          { value: "10", label: "Octobre" },
          { value: "11", label: "Novembre" },
          { value: "12", label: "Décembre" },
        ];
      }

      // S'assurer que nous avons bien récupéré les agences
      const agencies = contractsResponse.data.success
        ? contractsResponse.data.data.agencies || []
        : [];

      // Préparer la réponse
      const result = {
        success: true,
        data: {
          agencies,
          years: yearsAndMonths.years,
          months: yearsAndMonths.months,
        },
      };

      // Stocker la réponse dans le cache
      cache.filterOptions = {
        data: result,
        timestamp: Date.now(),
      };

      return result;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des options de filtres:",
        error
      );

      // En cas d'erreur, renvoyer des valeurs par défaut pour que l'interface continue de fonctionner
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = 0; i < 5; i++) {
        years.push((currentYear - i).toString());
      }

      const months = [
        { value: "1", label: "Janvier" },
        { value: "2", label: "Février" },
        { value: "3", label: "Mars" },
        { value: "4", label: "Avril" },
        { value: "5", label: "Mai" },
        { value: "6", label: "Juin" },
        { value: "7", label: "Juillet" },
        { value: "8", label: "Août" },
        { value: "9", label: "Septembre" },
        { value: "10", label: "Octobre" },
        { value: "11", label: "Novembre" },
        { value: "12", label: "Décembre" },
      ];

      return {
        success: true,
        data: {
          agencies: [],
          years,
          months,
        },
      };
    }
  },

  // Cache spécialisé pour les filtres (plus long car données stables)
  filterCache: {
    baseOptions: null, // Cache pour agences + années (ne change que rarement)
    monthsCache: new Map(), // Cache pour mois par année
    daysCache: new Map(), // Cache pour jours par année/mois
    cacheTime: 300000, // 5 minutes pour les filtres (plus long)
    timestamp: 0,
  },

  // Obtenir les options de filtres dynamiques pour les états journaliers (VERSION OPTIMISÉE)
  getDailyStatesFilterOptions: async (params = {}) => {
    const startTime = Date.now();
    console.log("🚀 Début récupération filtres optimisée:", params);

    try {
      const { year, month } = params;

      // 1. CACHE NIVEAU 1: Agences + Années (données stables)
      if (
        !contractService.filterCache.baseOptions ||
        Date.now() - contractService.filterCache.timestamp >
          contractService.filterCache.cacheTime
      ) {
        console.log("💾 Chargement cache de base (agences + années)");
        const response = await api.get(
          "/contracts/daily-states/filter-options"
        );

        if (response.data.success) {
          contractService.filterCache.baseOptions = {
            agencies: response.data.data.agencies,
            years: response.data.data.years,
          };
          contractService.filterCache.timestamp = Date.now();
        }
      }

      let result = {
        success: true,
        data: {
          agencies: contractService.filterCache.baseOptions.agencies,
          years: contractService.filterCache.baseOptions.years,
          months: [],
          days: [],
        },
      };

      // 2. CACHE NIVEAU 2: Mois pour une année donnée
      if (year && year !== "" && year !== "undefined") {
        const monthsCacheKey = `months-${year}`;

        if (!contractService.filterCache.monthsCache.has(monthsCacheKey)) {
          console.log(`💾 Chargement cache mois pour année ${year}`);
          const response = await api.get(
            "/contracts/daily-states/filter-options",
            {
              params: { year },
            }
          );

          if (response.data.success) {
            contractService.filterCache.monthsCache.set(
              monthsCacheKey,
              response.data.data.months
            );
          }
        }

        result.data.months =
          contractService.filterCache.monthsCache.get(monthsCacheKey) || [];
      }

      // 3. CACHE NIVEAU 3: Jours pour une année/mois donnée
      if (
        year &&
        month &&
        year !== "" &&
        month !== "" &&
        year !== "undefined" &&
        month !== "undefined"
      ) {
        const daysCacheKey = `days-${year}-${month}`;

        if (!contractService.filterCache.daysCache.has(daysCacheKey)) {
          console.log(`💾 Chargement cache jours pour ${year}/${month}`);
          const response = await api.get(
            "/contracts/daily-states/filter-options",
            {
              params: { year, month },
            }
          );

          if (response.data.success) {
            contractService.filterCache.daysCache.set(
              daysCacheKey,
              response.data.data.days
            );
          }
        }

        result.data.days =
          contractService.filterCache.daysCache.get(daysCacheKey) || [];
      }

      console.log(`✅ Filtres récupérés en ${Date.now() - startTime}ms`);
      return result;
    } catch (error) {
      console.error("❌ Erreur lors de la récupération des filtres:", error);

      // Fallback avec valeurs par défaut
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const currentDay = new Date().getDate();

      return {
        success: true,
        data: {
          agencies: [],
          years: [currentYear.toString()],
          months: [
            {
              value: currentMonth.toString(),
              label: new Intl.DateTimeFormat("fr-FR", { month: "long" }).format(
                new Date(2000, currentMonth - 1, 1)
              ),
            },
          ],
          days: [
            { value: currentDay.toString(), label: currentDay.toString() },
          ],
        },
      };
    }
  },

  // Méthode pour vider le cache des filtres si nécessaire
  clearFilterCache: () => {
    contractService.filterCache.baseOptions = null;
    contractService.filterCache.monthsCache.clear();
    contractService.filterCache.daysCache.clear();
    contractService.filterCache.timestamp = 0;
    console.log("🗑️ Cache des filtres vidé");
  },

  // Méthode pour vider le cache (utile pour les tests ou en cas de problème)
  clearCache: () => {
    cache.contracts.clear();
    cache.filterOptions = null;
    cache.contractById.clear();
    cache.contractChanges.clear();
    console.log("Cache vidé");
  },

  // Invalider le cache lors d'ajout/suppression de photos
  invalidateContractCache: (contractId) => {
    cache.contractById.delete(contractId);

    // Invalider aussi les listes qui contiennent ce contrat
    for (const [key, cacheEntry] of cache.contracts.entries()) {
      if (
        cacheEntry.data &&
        cacheEntry.data.data &&
        Array.isArray(cacheEntry.data.data)
      ) {
        const contractExists = cacheEntry.data.data.some(
          (c) => c._id === contractId
        );
        if (contractExists) {
          cache.contracts.delete(key);
        }
      }
    }
  },

  // Méthode utilitaire pour invalider le cache des statistiques
  invalidateStatisticsCache: () => {
    // Supprime toutes les entrées de cache qui commencent par 'statistics-'
    for (const [key] of cache.contracts.entries()) {
      if (key.startsWith("statistics-")) {
        cache.contracts.delete(key);
      }
    }
    console.log("Cache des statistiques invalidé");
  },
};

export default contractService;
